import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';

/**
 * "Nepřijel" and "Zrušit rezervaci" — the legacy dashboard's q_noshow and
 * the Zrušeno status of its form. Offered only while the booking has no
 * car behind it (new/pending/paid, no real arrival). Each asks for a
 * reason (required for Zrušit since 31 October 2026, optional for Nepřijel), which goes to the journal, and confirms on the second
 * press the way the legacy's modal did.
 */
export function StatusButtons({ r, compact }: { r: ReservationRow; compact?: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'noshow' | 'canceled' | null>(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const eligible = ['new', 'pending', 'paid'].includes(r.status) && !r.arrivalReal;

  const m = useMutation({
    mutationFn: (to: 'noshow' | 'canceled') => api.setStatus(r.id, { to, note: note.trim() }),
    onSuccess: async (_rc, to) => {
      setDone(to === 'noshow' ? '✔ Označeno: nepřijel' : '✔ Rezervace zrušena');
      setMode(null);
      await qc.invalidateQueries();
    },
  });

  if (done) return <span className="arrived-mark">{done}</span>;
  if (!eligible) return null;

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;
  const cls = compact ? 'btn sm' : 'btn';

  if (!mode) {
    return (
      <span className="statusbtns" onClick={(e) => e.stopPropagation()}>
        <button type="button" className={cls} onClick={() => setMode('noshow')}>
          Nepřijel
        </button>
        <button type="button" className={`${cls} danger`} onClick={() => setMode('canceled')}>
          Zrušit rezervaci
        </button>
      </span>
    );
  }

  return (
    <form
      className="arrive-form"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending) m.mutate(mode);
      }}
    >
      <label>
        {mode === 'noshow' ? 'Nepřijel — důvod' : 'Zrušení — důvod'}
        {/* Zrušení needs a reason (the owner, 31 October 2026); the server refuses it without one too. */}
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={255}
          placeholder={mode === 'canceled' ? 'povinné — proč (např. duplicita k ID …)' : 'nepovinné'}
          required={mode === 'canceled'}
          minLength={mode === 'canceled' ? 3 : undefined}
          autoFocus
        />
      </label>
      <button type="submit" className={mode === 'canceled' ? 'btn danger' : 'btn primary'} disabled={m.isPending || (mode === 'canceled' && note.replace(/\u00a0/g, ' ').trim().length < 3)}>
        {m.isPending ? 'Ukládám…' : mode === 'noshow' ? 'Opravdu nepřijel' : 'Opravdu zrušit'}
      </button>
      <button type="button" className="btn" onClick={() => setMode(null)} disabled={m.isPending}>
        Zpět
      </button>
      {message && <span className="arrive-error">{message}</span>}
    </form>
  );
}
